import { useEffect, useState } from 'react'
import { loadPhotoCredits } from '../lib/api.js'

/**
 * Photo credits (D-143). Every wildlife photo on the fact pages comes from
 * Wikimedia Commons, and most of them are under a Creative Commons licence that
 * asks for the photographer's name, the licence by name, and a way back to the
 * original. That used to be a caption printed over the bottom of each photo.
 * CC lets the credit be given "in any manner reasonable to the medium" — 4.0
 * says a link to a page carrying the information will do — so it is given here,
 * once, in a form a grown-up can actually read and follow.
 *
 * The list is the whole 157 — every living animal, and the one person on the
 * roster (D-150) — public-domain photos included. Nobody is owed a credit for
 * those, and they say so; leaving them off would make the page a worse answer
 * to "where did these pictures come from".
 */
export default function Credits({ onBack }) {
  const [credits, setCredits] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    let live = true
    loadPhotoCredits()
      .then((c) => live && setCredits(c))
      .catch(() => live && setError('Could not load the photo credits. Are you online?'))
    return () => { live = false }
  }, [])

  const retry = () => {
    setError(null)
    loadPhotoCredits()
      .then(setCredits)
      .catch(() => setError('Could not load the photo credits. Are you online?'))
  }

  return (
    <section className="screen credits">
      <div className="credits__head">
        <h1>Photo credits</h1>
        <button className="btn" onClick={onBack}>◀ Back</button>
      </div>

      <p className="credits__intro">
        Every wildlife photo in this app comes from{' '}
        <a href="https://commons.wikimedia.org" target="_blank" rel="noopener noreferrer">Wikimedia Commons</a>.
        Thank you to the photographers below. Each name links to the original file, and each licence to its terms.
      </p>

      {error && (
        <p className="credits__empty" role="alert">
          {error} <button className="btn btn--small" onClick={retry}>Try again</button>
        </p>
      )}

      {!credits && !error && <p className="credits__empty">Loading…</p>}

      {credits && (
        <>
          <p className="credits__count">{credits.count} photos. The drawings of the animals are the app's own.</p>
          <ul className="credits__list">
            {credits.photos.map((p) => (
              <li key={p.slug} className="credits__row">
                <b>{p.name}</b>
                <span>
                  {p.author ? (
                    <a href={p.source_url} target="_blank" rel="noopener noreferrer">{p.author}</a>
                  ) : (
                    <a href={p.source_url} target="_blank" rel="noopener noreferrer">the original file</a>
                  )}
                  {' · '}
                  {p.license_url
                    ? <a href={p.license_url} target="_blank" rel="noopener noreferrer">{p.license}</a>
                    : p.license}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  )
}
